import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  Injector,
  OnInit,
  effect,
  inject,
  input,
  output,
  runInInjectionContext,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { GoogleMap, MapAdvancedMarker } from '@angular/google-maps';
import {
  MatAutocompleteModule,
  MatAutocompleteSelectedEvent,
} from '@angular/material/autocomplete';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { environment } from '@env/environment';
import { Loader } from '@googlemaps/js-api-loader';
import { HttpStoreFacade } from '@http/store/facade';
import { DEFAULT_EMPTY_LOCATION, DEFAULT_LAT_LNG } from '@utils/defaults';
import { LatLng, Location } from 'pueblo-types';
import {
  BehaviorSubject,
  debounceTime,
  defer,
  filter,
  map,
  switchMap,
  tap,
} from 'rxjs';

export const PLACE_RESULT_FIELDS = [
  'formattedAddress',
  'displayName',
  'location',
];

@Component({
  selector: 'hang-map',
  templateUrl: 'hang-map.component.html',
  styleUrl: 'hang-map.component.scss',
  imports: [
    GoogleMap,
    MatAutocompleteModule,
    MatIconModule,
    MatInputModule,
    MatFormFieldModule,
    MatProgressSpinnerModule,
    MapAdvancedMarker,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HangMap implements OnInit {
  private readonly httpStore = inject(HttpStoreFacade);
  private readonly injector = inject(Injector);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly locationInput =
    viewChild.required<ElementRef<HTMLInputElement>>('locationInput');

  private readonly loader = new Loader({
    apiKey: environment.mapsApiKey,
    version: 'weekly',
  });

  protected readonly locationOptions = signal<
    google.maps.places.AutocompleteSuggestion[]
  >([]);
  protected readonly locationNameInput$ = new BehaviorSubject('');

  protected mapOptions: google.maps.MapOptions = {
    mapId: '6ad70592b3ce2a6a',
    center: DEFAULT_LAT_LNG,
    zoom: 13,
    disableDefaultUI: true,
  };

  protected readonly DEFAULT_LAT_LNG = DEFAULT_LAT_LNG;
  protected readonly userMarkerCoords = toSignal<LatLng | undefined>(
    this.httpStore.userLocation$(),
  );
  protected readonly selectionMarkerCoords = signal<LatLng | undefined>(
    undefined,
  );

  readonly location = input.required<Location>();
  readonly height = input<number>();
  readonly collapsed = input(false);
  readonly enableSelection = input(false);
  readonly enableAutocomplete = input(false);
  readonly enableUserLocation = input(true);
  readonly autocompleteInput = input<HTMLInputElement>();

  readonly locationSelected = output<Location>();

  protected readonly map = viewChild.required<GoogleMap>('map');

  private geocoder?: google.maps.Geocoder;

  private selectedLocation: Location = DEFAULT_EMPTY_LOCATION;

  async ngOnInit(): Promise<void> {
    await this.initGeocoder();
    await this.initPlacesAndAutocomplete();

    runInInjectionContext(this.injector, () => {
      effect(() => {
        if (
          this.location().coords.lat === 0 &&
          this.location().coords.lng === 0
        ) {
          this.mapOptions = {
            ...this.mapOptions,
            center: this.userMarkerCoords(),
          };
        }

        if (
          this.location().coords.lat === this.selectedLocation.coords.lat &&
          this.location().coords.lng === this.selectedLocation.coords.lng
        )
          return;

        this.setSelectionMarker(this.location());

        this.mapOptions = {
          ...this.mapOptions,
          center: this.location().coords,
        };
      });
    });
  }

  private async initGeocoder(): Promise<void> {
    const { Geocoder } = await this.loader.importLibrary('geocoding');
    this.geocoder = new Geocoder();
  }

  private async initPlacesAndAutocomplete() {
    const googleMap = this.map().googleMap;
    if (!googleMap) return;

    const { AutocompleteSessionToken, AutocompleteSuggestion } =
      await this.loader.importLibrary('places');

    this.locationNameInput$
      .pipe(
        tap((value) => {
          if (!value) this.locationOptions.set([]);
        }),
        debounceTime(300),
        filter((value) => !!value),
        map((value) => ({
          input: value,
          sessionToken: new AutocompleteSessionToken(),
        })),
        switchMap((request) =>
          defer(
            async () =>
              await AutocompleteSuggestion.fetchAutocompleteSuggestions(
                request,
              ),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((value) => {
        this.locationOptions.set(value.suggestions);
      });
  }

  private async setSelectionMarker(location: Location) {
    if (!this.geocoder) return;

    this.selectionMarkerCoords.set(location.coords);

    if (location.address) return;

    const geocoderResponse = await this.geocoder.geocode({
      location: location.coords,
    });

    const firstGeocoderResponse = geocoderResponse.results[0];

    this.selectedLocation = {
      ...this.selectedLocation,
      coords: location.coords,
      address: firstGeocoderResponse.formatted_address ?? '',
    };

    const place = new google.maps.places.Place({
      id: firstGeocoderResponse.place_id,
    });

    const placeResult = (
      await place.fetchFields({ fields: PLACE_RESULT_FIELDS })
    ).place;

    if (placeResult) {
      this.selectedLocation = {
        ...this.selectedLocation,
        displayName: placeResult.displayName ?? '',
        address: placeResult.formattedAddress ?? '',
      };

      this.locationSelected.emit(this.selectedLocation);
    }
  }

  protected autocompleteDisplayFn(
    suggestion: google.maps.places.AutocompleteSuggestion,
  ): string {
    return suggestion.placePrediction?.mainText?.text ?? '';
  }

  protected async autocompleteOptionSelected(
    event: MatAutocompleteSelectedEvent,
  ) {
    const suggestion = event.option
      .value as google.maps.places.AutocompleteSuggestion;
    const place = (
      await suggestion.placePrediction
        ?.toPlace()
        .fetchFields({ fields: PLACE_RESULT_FIELDS })
    )?.place;

    const location = place?.location;
    if (!location) throw 'Failed to return location from Autocomplete';

    const selectionPosition = { lat: location.lat(), lng: location.lng() };

    this.selectionMarkerCoords.set(selectionPosition);

    this.mapOptions = {
      ...this.mapOptions,
      center: selectionPosition,
      zoom: 13,
    };

    this.selectedLocation = {
      displayName: place.displayName ?? '',
      address: place.formattedAddress ?? '',
      coords: selectionPosition,
    };

    this.locationSelected.emit(this.selectedLocation);
  }

  protected setSelectionMarkerFromClick(
    location: google.maps.MapMouseEvent,
  ): void {
    this.locationInput().nativeElement.value = '';
    if (this.enableSelection()) {
      this.setSelectionMarker({
        displayName: '',
        address: '',
        coords: {
          lat: location.latLng?.lat() ?? 0,
          lng: location.latLng?.lng() ?? 0,
        },
      });
    }
  }

  protected updateLocationName(event: Event) {
    if (
      event instanceof InputEvent &&
      event.target instanceof HTMLInputElement
    ) {
      this.locationNameInput$.next(event.target.value);
    }
  }
}
